/**
 * Language and locale utilities
 *
 * @type {ngService}
 */
export class TmrLanguage {
  static ngType = 'service'

  constructor(
    $rootScope,
    $translate,
    tmhDynamicLocale,
    tmrLanguages,
    UserStorage
  ) {
    'ngInject'
    Object.assign(this, {
      $rootScope,
      $translate,
      tmhDynamicLocale,
      tmrLanguages,
      UserStorage,
    })
  }

  /**
   * Initializes the language system and the default locale
   *
   * @returns {Promise} Promise resolved with initial language.
   */
  init() {
    if (this.initPromise !== undefined) return this.initPromise

    // Needs the logged user before initial setup:
    // - Set a language from the user when localStorage is empty.
    // - Defer application init after translate is ready, so app is hidden until
    //   translation strings are loaded.
    this.initPromise = Promise.all([
      this.UserStorage.getUser(),
      this.$translate.onReady(),
    ]).then(([user]) => {
      // Full language setup.
      let language = user.$getLanguage()
      if (language) this.change(language)
      // If no user language, only locale setup is needed.
      else this.tmhDynamicLocale.set(this.locale())
      return this.current()
    })
    return this.initPromise
  }

  /**
   * Get current language.
   *
   * @returns {string} The current language
   */
  current() {
    return this.$translate.use()
  }

  /**
   * Get current locale. Corresponds to language if no locale key in {@link tmrLanguages}
   *
   * @param {string} locale The current locale
   */
  locale(language = this.current()) {
    // $translate.onReady() is not reliable: current() --> $translate.use() may return undefined
    if (!language) language = this.$translate.fallbackLanguage()
    return this.tmrLanguages.languages[language].locale
      ? this.tmrLanguages.languages[language].locale
      : language
  }

  /**
   * Change the current language.
   *
   * @param {string} language The new language
   */
  change(language) {
    this.$translate.use(language)
    this.tmhDynamicLocale.set(this.locale(language))
  }

  /**
   * Changes language when the logged user changes. If user language is missing, changes to site default.
   *
   * @param {User} user
   */
  onLoggedUserUpdate(user) {
    this.change(user.$getLanguage() ?? this.$translate.preferredLanguage())
  }

  /**
   * Registers a listener to user change.
   */
  registerOnLoggedUserUpdate() {
    this.$rootScope.$on('loggeduserupdate', (event, user) =>
      this.onLoggedUserUpdate(user)
    )
  }
}

/**
 * Language utilities Run phase: Listen to user change event.
 *
 * @type {ngRun}
 */
const TmrLanguageRun = /*@ngInject*/ TmrLanguage =>
  TmrLanguage.registerOnLoggedUserUpdate()

export { TmrLanguageRun as run }
